import { useContext } from 'react';
import { NavLink } from 'react-router-dom';

import AppContext from "../../../store/app-context";
import AuthContext from "../../../store/auth-context";
import { getEffectiveRoles } from '../../../config/roles';

import './Menu.scss';
import { FaTimes } from 'react-icons/fa';
import {
    RiHomeLine,
    RiTruckLine,
    RiInboxLine,
    RiSendPlaneLine,
    RiBarChartBoxLine,
    RiBuilding2Line,
    RiSettings4Line,
    RiToolsLine,
    RiLogoutBoxRLine
} from 'react-icons/ri';

const Menu = () => {
    const appCtx = useContext(AppContext);
    const authCtx = useContext(AuthContext);

    const user = authCtx.user;
    const partner = authCtx.partner;

    const displayName = user?.name || 'User';
    const initials = displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

    const effectiveRoles = getEffectiveRoles(user?.role);

    const scannerNav = [
        { label: 'Home', path: '/', icon: <RiHomeLine />, roles: ['ADMIN', 'WAREHOUSE', 'COURIER'] },
        { label: 'Courier', path: '/courier', icon: <RiTruckLine />, roles: ['ADMIN', 'COURIER'] },
        { label: 'Incoming', path: '/incoming', icon: <RiInboxLine />, roles: ['ADMIN', 'WAREHOUSE'] },
        { label: 'Dispatch', path: '/dispatch', icon: <RiSendPlaneLine />, roles: ['ADMIN', 'WAREHOUSE'] },
    ];

    const dashboardNav = [
        { label: 'Courier Dashboard', path: '/dashboard/partner', icon: <RiBarChartBoxLine />, roles: ['ADMIN', 'COURIER'] },
        { label: 'Warehouse Dashboard', path: '/dashboard/warehouse', icon: <RiBuilding2Line />, roles: ['ADMIN', 'WAREHOUSE'] },
    ];

    // Configuration nav — role-aware. Visibility derives from `user.role`
    // (not the effective-roles map) because Driver and Master both expand to
    // the COURIER effective role; we need the literal role to distinguish
    // a Master (full access) from a Driver (read-only courier view).
    type ConfigurationItem = { label: string; path: string; allowedRoles: string[] };
    const configurationNav: ConfigurationItem[] = [
        { label: 'Vehicles', path: '/configuration/vehicles', allowedRoles: ['MASTER', 'DRIVER'] },
        { label: 'Service Areas', path: '/configuration/service-areas', allowedRoles: ['MASTER', 'DRIVER'] },
        { label: 'Pricing', path: '/configuration/pricing', allowedRoles: ['MASTER', 'DRIVER', 'WAREHOUSE_STAFF'] },
    ];
    const visibleConfiguration = configurationNav.filter(item =>
        user?.role ? item.allowedRoles.includes(user.role) : false
    );

    const getDisplayRole = () => {
        if (!user?.role) return '';
        const roleLabels: Record<string, string> = {
            'MASTER': 'Partner Admin',
            'DRIVER': 'Driver',
            'WAREHOUSE_STAFF': 'Warehouse Staff',
        };
        return roleLabels[user.role] || user.role;
    };

    const renderNavItem = (item: typeof scannerNav[0]) => {
        const hasAccess = effectiveRoles.some(role => item.roles.includes(role));
        if (!user || !hasAccess) return null;

        return (
            <li key={item.path}>
                <NavLink
                    to={item.path}
                    className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
                    onClick={() => appCtx.toggleMenu(false)}
                    end={item.path === '/'}
                >
                    <span className="menu-icon">{item.icon}</span>
                    <span className="menu-label">{item.label}</span>
                </NavLink>
            </li>
        );
    };

    const visibleDashboards = dashboardNav.filter(item =>
        effectiveRoles.some(role => item.roles.includes(role))
    );

    return (
        <div id="menu" className={appCtx.hideMenu ? 'hide' : ''}>
            <div className="inner">
                <div className="panel">
                    <button className="close-btn" onClick={() => appCtx.toggleMenu(false)} aria-label="Close menu">
                        <FaTimes />
                    </button>

                    {user && (
                        <div className="user-section">
                            <div className="user-avatar">{initials}</div>
                            <div className="user-details">
                                <span className="user-name">{displayName}</span>
                                <span className="user-role">{getDisplayRole()}</span>
                            </div>
                            {partner && (
                                <div className="partner-badge">{partner.name}</div>
                            )}
                        </div>
                    )}

                    <nav className="menu-nav">
                        <div className="nav-section">
                            <span className="section-label">Scanner</span>
                            <ul>{scannerNav.map(renderNavItem)}</ul>
                        </div>

                        {visibleDashboards.length > 0 && authCtx.dashboardEnabled && (
                            <div className="nav-section">
                                <span className="section-label">Dashboards</span>
                                <ul>{dashboardNav.map(renderNavItem)}</ul>
                            </div>
                        )}

                        {visibleConfiguration.length > 0 && (
                            <div className="nav-section">
                                <span className="section-label">Configuration</span>
                                <ul>
                                    {visibleConfiguration.map(item => (
                                        <li key={item.path}>
                                            <NavLink
                                                to={item.path}
                                                className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
                                                onClick={() => appCtx.toggleMenu(false)}
                                            >
                                                <span className="menu-icon"><RiToolsLine /></span>
                                                <span className="menu-label">{item.label}</span>
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="nav-section">
                            <ul>
                                <li>
                                    <NavLink
                                        to="/settings"
                                        className={({ isActive }) => `menu-item ${isActive ? 'active' : ''}`}
                                        onClick={() => appCtx.toggleMenu(false)}
                                    >
                                        <span className="menu-icon"><RiSettings4Line /></span>
                                        <span className="menu-label">Settings</span>
                                    </NavLink>
                                </li>
                            </ul>
                        </div>
                    </nav>

                    {user && (
                        <div className="menu-footer">
                            <button className="logout-btn" onClick={authCtx.logOut}>
                                <RiLogoutBoxRLine />
                                <span>Log Out</span>
                            </button>
                        </div>
                    )}
                </div>
                <div className="screen" onClick={() => appCtx.toggleMenu(false)}></div>
            </div>
        </div>
    );
};

export default Menu;
